// EnterLeadDetails.jsx
import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import {
  sendOtpToLeadMobile,
  verifyOtpToLeadMobile,
  sendOtpToLeadEmail,
  verifyOtpToLeadEmail,
  commonUrl,
} from "../../../utils/urls";
import OtpModal from "./OtpModal";
import CustomSelectBasic from "../../../components/common/CustomSelect/CustomSelectBasic";
import CustomDatePicker from "../../../components/common/CustomDatePicker/CustomDatePicker";
import CustomTextInput from "../../../components/common/CustomTextInput";
import CustomButton from "../../../components/common/CustomButton/CustomButton";
import { errorToast } from "../../../utils/toasts";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const validationSchema = Yup.object({
  name: Yup.string().required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
  email: Yup.string().matches(emailRegex, "Invalid email").required("Required"),
  contact_number: Yup.string()
    .matches(/^\d{10}$/, "Must be 10 digits")
    .required("Required"),
  dob: Yup.string().required("Required"),
  address: Yup.string().required("Required").min(5, "Minimum 5 characters"),
  city_id: Yup.string().required("Required"),
  state_id: Yup.string().required("Required"),
});

const EnterLeadDetails = ({
  onNext,
  onCloseModal,
  onUpdate,
  initialValues,
}) => {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [emailOtpVisible, setEmailOtpVisible] = useState(false);
  const [mobileOtpVisible, setMobileOtpVisible] = useState(false);
  const [mobileToken, setMobileToken] = useState("");
  const [emailToken, setEmailToken] = useState("");
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useState(false);

  React.useEffect(() => {
    axios.get(`${commonUrl}/get-state-with-city`).then((res) => {
      const activeStates = res.data.data.filter(
        (s) => s.is_active && !s.is_delete
      );
      setStates(activeStates);
    });
  }, []);

  const handleVerifyOtp = async (type, otp, value, onClose) => {
    const url =
      type === "mobile" ? verifyOtpToLeadMobile : verifyOtpToLeadEmail;
    const payload = {
      token: type === "mobile" ? mobileToken : emailToken,
      otp: Number(otp),
      [type === "mobile" ? "mobile_number" : "email"]: value,
    };
    try {
      const res = await axios.post(url, payload);
      if (res.data.success) {
        if (type === "mobile") setIsMobileVerified(true);
        else setIsEmailVerified(true);
        onClose();
      }
    } catch (error) {
      console.log(error?.response);

      errorToast(
        error?.response?.data?.error?.message ||
          "Something went wrong, try again"
      );
    }
  };

  const handleSendOtp = async (type, value) => {
    if (!value) {
      errorToast("Please enter a valid mobile number or email");
      return;
    }
    try {
      const url = type === "mobile" ? sendOtpToLeadMobile : sendOtpToLeadEmail;
      const res = await axios.post(url, {
        [type === "mobile" ? "mobile_number" : "email"]: value,
      });
      if (res.data.success) {
        const token = res.data.data.token;
        if (type === "mobile") {
          setMobileToken(token);
          setMobileOtpVisible(true);
        } else {
          setEmailToken(token);
          setEmailOtpVisible(true);
        }
      }
    } catch (error) {
      console.log(error?.response);

      errorToast(
        error?.response?.data?.error?.message ||
          "Something went wrong, try again"
      );
    }
  };

  return (
    <Formik
      initialValues={{
        name: initialValues?.name || "",
        email: initialValues?.email || "",
        contact_number: initialValues?.contact_number || "",
        dob: initialValues?.dob || "",
        address: initialValues?.address || "",
        state_id: initialValues?.state_id || "",
        city_id: initialValues?.city_id || "",
      }}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        if (!isEmailVerified || !isMobileVerified) return;
        onUpdate(values, states?.find((s) => s.id === values?.state_id), cities?.find((c) => c.id === values?.city_id));
        onNext();
      }}
    >
      {({ values, setFieldValue, errors, touched, validateField }) => {
        const handleInputChange = (e) => {
          const { name, value } = e.target;
          setFieldValue(name, value);
        };

        return (
          <div className="pt-4 border-t border-[#E2EBF3]">
            <p className="text-[1.rem] font-[600] text-[#2C2C2C] mb-4">
              Enter Lead Details{" "}
            </p>
            <Form className="space-y-4">
              <div className=" h-[calc(100vh_-_24rem)] overflow-y-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <CustomTextInput
                    name="name"
                    label="Full Name"
                    placeholder="Enter full name"
                    required
                  />
                  <CustomTextInput
                    name="contact_number"
                    label="Mobile No."
                    placeholder="Enter mobile number"
                    required
                    endIcon={isMobileVerified ? "✔️" : <p style={verifyButtonStyle}>Verify</p>}
                    onInput={(e) => {
                      e.target.value = e.target.value.slice(0, 10);
                    }}
                    onEndIconClick={async () => {
                      if (values.contact_number?.length !== 10)
                        return errorToast("Please enter a valid mobile number");

                      handleSendOtp("mobile", values.contact_number);
                    }}
                  />
                  <CustomTextInput
                    name="email"
                    label="Email ID"
                    placeholder="Enter email"
                    required
                    endIcon={isEmailVerified ? "✔️" : <p style={verifyButtonStyle}>Verify</p>}
                    onEndIconClick={() => {
                      if (!values.email?.match(emailRegex)) {
                        errorToast("Please enter a valid email");
                      }
                      handleSendOtp("email", values.email);
                    }}
                  />
                  <CustomDatePicker
                    required
                    name="dob"
                    label="Date of Birth"
                    placeholder="Select date"
                    type="date"
                  />
                  <CustomTextInput
                    name="address"
                    label="Address"
                    placeholder="Enter address"
                    required
                  />
                  <CustomSelectBasic
                    required
                    value={values?.state_id}
                    name="state_id"
                    label="State"
                    placeholder="Select State"
                    menuList={states.map((state) => ({
                      label: state.title,
                      value: state.id,
                    }))}
                    onChange={(_val) => {
                      if (_val?.target?.value) {
                        setFieldValue("state_id", _val?.target?.value);
                        const city = states.find(
                          (f) => f?.id === _val?.target?.value
                        )?.city;
                        console.log(city);

                        setCities(city);
                      }
                    }}
                  />
                  <CustomSelectBasic
                    required
                    value={values?.city_id}
                    disabled={!values?.state_id}
                    name="city_id"
                    label="City"
                    placeholder="Select city"
                    menuList={cities.map((city) => ({
                      label: city.title,
                      value: city.id,
                    }))}
                    onChange={(_val) => {
                      if (_val?.target?.value) {
                        setFieldValue("city_id", _val?.target?.value);
                      }
                    }}
                  />
                </div>
              </div>

              <div className="flex justify-between gap-4">
                {/* <button type="submit" className="btn-primary" disabled={!isEmailVerified || !isMobileVerified}>
                  Save & Next
                </button> */}
                <CustomButton
                  type="button"
                  variant="outlined"
                  label="Close"
                  onClick={onCloseModal}
                />
                <CustomButton
                  type="submit"
                  label="Save & Next"
                  disabled={!isEmailVerified || !isMobileVerified}
                />
              </div>
              {emailOtpVisible && (
                <OtpModal
                  type="email"
                  value={values.email}
                  onClose={() => setEmailOtpVisible(false)}
                  onVerify={handleVerifyOtp}
                />
              )}
              {mobileOtpVisible && (
                <OtpModal
                  type="mobile"
                  value={values.contact_number}
                  onClose={() => setMobileOtpVisible(false)}
                  onVerify={handleVerifyOtp}
                />
              )}
            </Form>
          </div>
        );
      }}
    </Formik>
  );
};

export default EnterLeadDetails;

const verifyButtonStyle = {
  padding: '0.5rem 0.5rem',
position: 'relative',
marginTop: '-0.6rem',
marginRight: '-0.6rem',
borderLeft: 'solid 1px #d1d5db',
color: "#152B47"
}
